/**
 * Instant Battle chooser (WP-11). A settings-style modal window (dim backdrop +
 * centered panel) that lets the player pick one of the themed instant battles
 * (rules/instant.ts), the side to command, and the AI difficulty, then launches
 * it. Pure presentation: it owns no game state — it just calls
 * `onStart({ battle, human, level })`, or `onExit()` when the window is closed.
 */
import { DIFFICULTY_NAMES, DEFAULT_DIFFICULTY } from '../rules/ai';
import { INSTANT_BATTLES, type InstantBattle } from '../rules/instant';
import type { Side } from '../rules/state';
import { fitScreen } from './screen';
import { ICON_ZAP, ICON_VP, ICON_PLAY } from './icons';

// Header close glyph + the team chips (colour + O/X symbol) shared with the game
// scoreboard — the two sides read apart by shape as well as colour, no names.
const CLOSE_ICON = '<svg class="bico" viewBox="0 0 12 12" aria-hidden="true"><path d="M3.2 3.2 L8.8 8.8 M8.8 3.2 L3.2 8.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const SHAPE_O = '<svg viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="3.4" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
const SHAPE_X = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3.2 3.2 L8.8 8.8 M8.8 3.2 L3.2 8.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

const CSS = `
.ar-instant { font-family: Georgia, 'Times New Roman', serif; color: hsl(28, 55.6%, 10.6%);
  --navy:hsl(217, 53.7%, 16.1%); --ink:hsl(219, 58.3%, 9.4%); --parch:hsl(44, 53.7%, 83.9%); --parch2:hsl(40, 46.9%, 71.2%);
  --wood:hsl(31, 47.6%, 28.4%); --wood-d:hsl(30, 54.2%, 18.8%); --brass:hsl(43, 63.2%, 58.4%); --brass-l:hsl(46, 80.7%, 73.5%); --brass-d:hsl(42, 61.6%, 34.7%);
  --head:hsl(23, 66.7%, 21.2%); --txt:hsl(28, 55.6%, 10.6%); --redx:hsl(7, 70.6%, 41.4%); --yelx:hsl(44, 84.4%, 37.6%);
  opacity:1; transition: opacity 0.22s ease; }
.ar-instant.leaving { opacity:0; }
/* dim full-viewport backdrop holding the centered window; click-outside closes —
   exactly the settings-window overlay. */
.ar-instant-backdrop { position:fixed; inset:0; z-index:50;
  background: radial-gradient(ellipse at center, hsla(219, 51.8%, 16.3%, 0.72), hsla(220, 60%, 3.9%, 0.86));
  opacity:1; transition: opacity 0.22s ease; }
.ar-instant-backdrop.leaving { opacity:0; }
/* header mirrors the settings/campaign bar: an optional back button + a beveled
   groove + a left-aligned heading with an icon. */
.ar-instant .hd { display:flex; flex-wrap:wrap; align-items:stretch;
  background: linear-gradient(180deg,hsl(219, 52.2%, 22.2%),var(--navy)); color:hsl(43, 100%, 84.5%);
  border:2px solid var(--ink); border-bottom:3px solid var(--brass-d); }
.ar-instant .hd h2 { margin:0; flex:1; display:flex; align-items:center; gap:8px; font-size:22px; letter-spacing:1px;
  padding:11px 18px; text-shadow:0 1px 0 hsl(0, 0%, 0%); }
.ar-instant .hd h2.groove { border-left:1px solid hsla(0, 0%, 0%, 0.35); box-shadow: inset 1px 0 0 hsla(0, 0%, 100%, 0.12); }
.ar-instant .hd h2 .ic { display:inline-flex; } .ar-instant .hd h2 .ic svg { width:21px; height:21px; display:block; }
/* back button — the same red chrome as the settings header's close/back button */
.ar-instant .ar-cbtn { align-self:center; margin-left:14px; font-family:inherit; font-weight:700; font-size:15px; padding:9px 16px; cursor:pointer; color:#fff;
  background:linear-gradient(180deg, hsl(4,62%,55%), hsl(4,62%,47%) 55%, hsl(4,62%,39%)); border:2px solid hsl(4,48%,30%); border-radius:4px;
  box-shadow: inset 0 1px 0 hsla(0,0%,100%,0.4), inset 0 -2px 2px hsla(0,0%,0%,0.22), 0 1px 2px hsla(0,0%,0%,0.3);
  text-shadow: 0 1px 1px hsla(0,0%,0%,0.45); display:inline-flex; align-items:center; gap:8px; transition:filter .08s; }
.ar-instant .ar-cbtn:hover { filter:brightness(1.08); } .ar-instant .ar-cbtn:active { transform:translateY(1px); }
.ar-instant .ar-cbtn .bico { width:14px; height:14px; display:block; }
/* body (parchment) */
.ar-instant .body { background: linear-gradient(180deg,hsl(44, 55.6%, 89.4%),var(--parch) 30%,var(--parch2));
  border:2px solid var(--wood); border-top:none; padding:15px 18px 18px; }
.ar-instant .intro { margin:0 0 14px; font-size:13px; font-style:italic; color:var(--wood-d); line-height:1.45; }
/* the currency star inherits the surrounding text colour (like everywhere else) —
   no special green — reads a touch larger than the prose, sits upright (not
   italic) and vertically centred on the line. */
.ar-instant .intro .vp { display:inline-flex; align-items:center; vertical-align:middle; font-style:normal; }
.ar-instant .intro .vp svg { width:1.3em; height:1.3em; display:block; }
/* battle cards */
.ar-instant .cards { display:flex; gap:14px; }
.ar-instant .card { flex:1; min-width:0; background: linear-gradient(180deg,hsl(46, 72.4%, 94.3%),hsl(43, 50.5%, 81%));
  border:2px solid var(--wood); border-radius:6px; padding:13px 14px; cursor:pointer;
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.5); transition: filter .08s, box-shadow .08s; }
.ar-instant .card:hover { filter:brightness(1.04); }
.ar-instant .card.sel { border-color:hsl(105, 41.7%, 29.6%); box-shadow:0 0 0 3px hsl(105, 40.6%, 58.4%) inset, inset 0 1px 0 hsla(0, 0%, 100%, 0.5); }
.ar-instant .card h3 { margin:0 0 6px; color:var(--head); font-size:17px; display:flex; align-items:center; gap:7px; }
.ar-instant .card .tick { margin-left:auto; flex:0 0 auto; width:20px; height:20px; border-radius:50%; display:none;
  align-items:center; justify-content:center; font-size:13px; color:#fff; background:hsl(124, 44.4%, 33.1%); border:1px solid #fff; }
.ar-instant .card.sel .tick { display:inline-flex; }
.ar-instant .card .blurb { font-size:12.5px; line-height:1.45; color:var(--txt); }
.ar-instant .card .themes { margin-top:10px; display:flex; flex-wrap:wrap; gap:6px; }
.ar-instant .card .tag { display:inline-flex; align-items:center; gap:6px; padding:2px 9px 2px 3px; border-radius:12px; font-size:11.5px; font-weight:700;
  color:var(--wood-d); border:1px solid var(--wood); background:hsla(0, 0%, 100%, 0.4); }
/* shared team chip (colour + O/X symbol) — used by the side picker and card tags;
   the two sides read apart by shape as well as colour, so no names are needed. */
.ar-instant .ar-team-chip { flex:0 0 auto; border-radius:4px; border:1px solid hsla(0, 0%, 0%, 0.55);
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.45), 0 1px 2px hsla(0, 0%, 0%, 0.4); display:inline-flex; align-items:center; justify-content:center; }
.ar-instant .ar-team-chip svg { width:72%; height:72%; display:block; }
.ar-instant .ar-team-chip.R { background: linear-gradient(180deg, hsl(7, 72.2%, 56.3%), var(--redx)); color:#fff; }
.ar-instant .ar-team-chip.Y { background: linear-gradient(180deg, hsl(44, 86.3%, 68.6%), var(--yelx)); color:hsl(42, 81.3%, 12.5%); }
.ar-instant .card .tag .ar-team-chip { width:16px; height:16px; border-radius:3px; }
/* options: command side + enemy admiral */
.ar-instant .opts { display:flex; gap:22px; align-items:center; margin-top:16px; flex-wrap:wrap; }
.ar-instant .opt { display:flex; align-items:center; gap:9px; }
.ar-instant .opt > label { font-weight:700; color:var(--head); font-size:14px; }
/* side picker: two team-chip buttons. The chosen one is raised with a green
   selection ring; the other is dimmed, so which side you command is unmistakable. */
.ar-instant .side { display:inline-flex; gap:8px; }
.ar-instant .side button { display:inline-flex; align-items:center; justify-content:center; padding:6px; cursor:pointer;
  border:2px solid var(--wood-d); border-radius:6px; background:linear-gradient(180deg, hsl(44, 58.4%, 84.9%), hsl(40, 46.9%, 71.2%));
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.5); transition: filter .08s, opacity .08s; }
.ar-instant .side button:not(.on) { opacity:0.5; }
.ar-instant .side button:not(.on):hover { opacity:0.8; }
.ar-instant .side button.on { border-color:hsl(105, 41.7%, 29.6%);
  box-shadow: 0 0 0 2px hsl(105, 40.6%, 58.4%) inset, inset 0 1px 0 hsla(0, 0%, 100%, 0.5); }
.ar-instant .side .ar-team-chip { width:26px; height:26px; }
.ar-instant select { font-family:inherit; font-size:14px; padding:6px 10px; color:var(--txt);
  border:1px solid var(--wood-d); border-radius:4px; background:hsl(200, 100%, 89%); box-shadow:inset 0 1px 0 hsla(0, 0%, 100%, 0.5); cursor:pointer; }
/* go row — the primary action (back lives in the header); the CTA reads large. */
.ar-instant .go { display:flex; justify-content:flex-end; margin-top:18px; }
.ar-instant .ar-btn { font-family:inherit; font-weight:700; font-size:16px; padding:12px 22px; cursor:pointer; color:var(--txt);
  background:linear-gradient(180deg,var(--brass-l),var(--brass) 55%,var(--brass-d)); border:2px solid var(--wood-d); border-radius:4px;
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.55), inset 0 -2px 2px hsla(0, 0%, 0%, 0.18), 0 1px 2px hsla(0, 0%, 0%, 0.3);
  display:inline-flex; align-items:center; gap:8px; transition:filter .08s; }
.ar-instant .ar-btn.primary { color:hsl(90, 100%, 95.7%); background:linear-gradient(180deg,hsl(105, 47.8%, 64.7%),hsl(105, 38.4%, 43.9%) 55%,hsl(105, 41.7%, 29.6%)); border-color:hsl(110, 40.6%, 25.1%); }
.ar-instant .ar-btn:hover { filter:brightness(1.06); } .ar-instant .ar-btn:active { transform:translateY(1px); }
.ar-instant .ar-btn .ico { display:inline-flex; } .ar-instant .ar-btn .ico svg { width:20px; height:20px; display:block; }
@media (max-width: 560px) {
  .ar-instant .cards { flex-direction: column; }
  .ar-instant .hd h2 { font-size:18px; padding:9px 12px; }
  .ar-instant .go .ar-btn { flex:1 1 auto; justify-content:center; }
}
`;

export interface InstantChoice {
  battle: InstantBattle;
  human: Side;
  level: number;
}

export interface InstantChooserOpts {
  container: HTMLElement;
  onStart: (choice: InstantChoice) => void;
  /** optional "back to quick battle" link */
  onExit?: () => void;
}

export interface InstantChooserHandle {
  /** current selection (exposed for the E2E) */
  choice(): InstantChoice;
  /** launch the selected battle immediately */
  start(): void;
  destroy(): void;
}

export function showInstantChooser(opts: InstantChooserOpts): InstantChooserHandle {
  if (!document.getElementById('ar-instant-style')) {
    const style = document.createElement('style');
    style.id = 'ar-instant-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  let battle: InstantBattle = INSTANT_BATTLES[0]!.id;
  let human: Side = 'R';
  let level = DEFAULT_DIFFICULTY;

  const wrap = document.createElement('div');
  wrap.className = 'ar-instant';
  wrap.id = 'ar-instant';
  const cards = INSTANT_BATTLES.map(
    (b) => `<div class="card" data-battle="${b.id}">
        <h3>${b.name}<span class="tick">✓</span></h3>
        <div class="blurb">${b.blurb}</div>
        <div class="themes">
          <span class="tag"><i class="ar-team-chip R">${SHAPE_O}</i>${b.redTheme}</span>
          <span class="tag"><i class="ar-team-chip Y">${SHAPE_X}</i>${b.yellowTheme}</span>
        </div>
      </div>`,
  ).join('');
  const diffOpts = DIFFICULTY_NAMES.map(
    (n, i) => `<option value="${i + 1}"${i + 1 === level ? ' selected' : ''}>Lv ${i + 1} — ${n}</option>`,
  ).join('');
  wrap.innerHTML = `
    <div class="hd">
      <button class="ar-cbtn" id="ar-inst-back" aria-label="Close">${CLOSE_ICON}Close</button>
      <h2 class="groove"><span class="ic">${ICON_ZAP}</span>Instant Battle</h2>
    </div>
    <div class="body">
      <p class="intro">Jump straight into the middle of an intense fight — no fleet-building, no map conquest. Every turn must be fought, and it can’t be projected — squeeze out every <span class="vp">${ICON_VP}</span>.</p>
      <div class="cards">${cards}</div>
      <div class="opts">
        <div class="opt"><label>You command</label>
          <span class="side" id="ar-side">
            <button data-side="R" aria-label="Command the red fleet"><i class="ar-team-chip R">${SHAPE_O}</i></button>
            <button data-side="Y" aria-label="Command the yellow fleet"><i class="ar-team-chip Y">${SHAPE_X}</i></button>
          </span>
        </div>
        <div class="opt"><label>Enemy admiral</label>
          <select id="ar-inst-level">${diffOpts}</select>
        </div>
      </div>
      <div class="go">
        <button class="ar-btn primary" id="ar-inst-go"><span class="lbl">Begin battle</span><span class="ico">${ICON_PLAY}</span></button>
      </div>
    </div>`;
  // Mount as a settings-style modal: a dim backdrop holding the centred window.
  const backdrop = document.createElement('div');
  backdrop.className = 'ar-instant-backdrop';
  backdrop.appendChild(wrap);
  opts.container.appendChild(backdrop);
  const stopFit = fitScreen(wrap, { center: true });
  wrap.style.maxWidth = '880px'; // a centred window, not a full-width strip

  const refresh = (): void => {
    wrap.querySelectorAll<HTMLElement>('.card').forEach((el) => {
      el.classList.toggle('sel', el.dataset['battle'] === battle);
    });
    wrap.querySelectorAll<HTMLButtonElement>('#ar-side button').forEach((b) => {
      b.classList.toggle('on', b.dataset['side'] === human);
    });
  };

  wrap.querySelectorAll<HTMLElement>('.card').forEach((el) => {
    el.addEventListener('click', () => {
      battle = el.dataset['battle'] as InstantBattle;
      refresh();
    });
  });
  wrap.querySelectorAll<HTMLButtonElement>('#ar-side button').forEach((b) => {
    b.addEventListener('click', () => {
      human = b.dataset['side'] as Side;
      refresh();
    });
  });
  wrap.querySelector<HTMLSelectElement>('#ar-inst-level')!.addEventListener('change', (ev) => {
    level = Number((ev.target as HTMLSelectElement).value);
  });
  const start = (): void => opts.onStart({ battle, human, level });
  wrap.querySelector('#ar-inst-go')!.addEventListener('click', start);
  // Close (or click-outside) fades the window out, then hands back — matching the
  // settings overlay. The empty margin above/below the panel is the wrap's own
  // flex area, so a click there counts as "outside" too.
  const leave = (then: () => void): void => {
    wrap.classList.add('leaving');
    backdrop.classList.add('leaving');
    window.setTimeout(then, 230);
  };
  const close = (): void => { if (opts.onExit) leave(opts.onExit); };
  wrap.querySelector('#ar-inst-back')?.addEventListener('click', close);
  backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop || ev.target === wrap) close(); });
  refresh();

  return {
    choice: () => ({ battle, human, level }),
    start,
    destroy: () => { stopFit(); backdrop.remove(); },
  };
}
